const FIRST_PAGE_STORAGE_KEY = 'internal_first_traffic_source_page'

/** 路由 path → 页面编码（与 BI pageViewList.type 对齐） */
const ROUTE_PAGE_CODE_RULES: Array<{ match: (path: string) => boolean; pageCode: string }> = [
  { match: (p) => p === '/' || p === '', pageCode: 'home' },
  { match: (p) => p.startsWith('/search-input'), pageCode: 'searchInput' },
  { match: (p) => p.startsWith('/search'), pageCode: 'searchProductResults' },
  { match: (p) => p.startsWith('/listing'), pageCode: 'productDetail' },
  { match: (p) => p.startsWith('/flash-sales'), pageCode: 'flashSale' },
  { match: (p) => p.startsWith('/new-arrival'), pageCode: 'newArrival' },
  { match: (p) => p.startsWith('/shopping-cart'), pageCode: 'cart' },
  { match: (p) => p.startsWith('/wish-list'), pageCode: 'productFavoriteList' },
  { match: (p) => p.startsWith('/store/'), pageCode: 'storeHome' },
  { match: (p) => p.startsWith('/category/'), pageCode: 'searchCategory' },
  { match: (p) => p.startsWith('/all-category'), pageCode: 'allCategoriesNavigation' },
  { match: (p) => p.startsWith('/message'), pageCode: 'messageCenter' },
  { match: (p) => p.startsWith('/checkout'), pageCode: 'orderSubmit' },
  { match: (p) => p.startsWith('/user-center'), pageCode: 'accountCenter' },
  { match: (p) => p.startsWith('/recent-views'), pageCode: 'historyViewList' },
  { match: (p) => p.startsWith('/reviews-publish'), pageCode: 'pendingReviewProductsList' },
]

/** 结构化 traffic source（url-cache 存储，value 可含任意字符） */
export interface ISourceMeta {
  module: string
  type: string
  value: string
  firstPage: string
  latestPage: string
}

/** module|type|value|firstPage|latestPage → module-type-value-firstPage-latestPage */
export function encodeSource(source?: string): string | undefined {
  if (!source) return undefined
  return source.split('|').join('-')
}

/** 将 source 字符串解析为 5 段（value 可能含 `-`/`|`，从首尾固定段反推） */
export function parseSourceToParts(source?: string): [string, string, string, string, string] {
  if (!source) return ['', '', '', '', '']

  const sep = source.includes('|') ? '|' : '-'
  const parts = source.split(sep)

  if (parts.length <= 3) {
    return [parts[0] || '', parts[1] || '', parts[2] || '', '', '']
  }

  return [
    parts[0] || '',
    parts[1] || '',
    parts.slice(2, parts.length - 2).join(sep),
    parts[parts.length - 2] || '',
    parts[parts.length - 1] || ''
  ]
}

export function parseSourceToMeta(source?: string): ISourceMeta | undefined {
  if (!source) return undefined
  const [module, type, value, firstPage, latestPage] = parseSourceToParts(source)
  return { module, type, value, firstPage, latestPage }
}

/** 优先使用结构化 meta，否则回退解析 source 字符串 */
export function getTrafficSourceParts(
  source?: string,
  meta?: ISourceMeta | null
): [string, string, string, string, string] {
  if (meta && (meta.module || meta.type || meta.value || meta.firstPage || meta.latestPage)) {
    return [
      meta.module || '',
      meta.type || '',
      meta.value || '',
      meta.firstPage || '',
      meta.latestPage || ''
    ]
  }
  return parseSourceToParts(source || '')
}

/** get-params 对外展开为 5 个独立字段（不拼 source 字符串，避免 value 含 `-`/`|` 歧义） */
export function expandSourceMetaToQuery(meta: ISourceMeta) {
  return {
    sourceModule: meta.module,
    sourceType: meta.type,
    sourceValue: meta.value,
    sourceFirstPage: meta.firstPage,
    sourceLatestPage: meta.latestPage,
  }
}

/** @deprecated 使用 parseSourceToParts */
export function sourceToPipeParts(source?: string): [string, string, string, string, string] {
  return parseSourceToParts(source)
}

/** 从 get-params 返回的 query 构建结构化 meta */
export function buildSourceMetaFromQuery(query: Record<string, unknown>): ISourceMeta | undefined {
  if (
    query.sourceModule == null
    && query.sourceType == null
    && query.sourceValue == null
    && query.sourceFirstPage == null
    && query.sourceLatestPage == null
  ) {
    return undefined
  }

  return {
    module: String(query.sourceModule ?? ''),
    type: String(query.sourceType ?? ''),
    value: String(query.sourceValue ?? ''),
    firstPage: String(query.sourceFirstPage ?? ''),
    latestPage: String(query.sourceLatestPage ?? ''),
  }
}

/** 路由 path → 页面编码 */
export function resolvePageCodeFromPath(path: string): string {
  const rule = ROUTE_PAGE_CODE_RULES.find((r) => r.match(path))
  return rule?.pageCode || ''
}

/** 当前路由对应的页面编码；RULES 未匹配时回退 route.name */
export function resolveCurrentPageCode(route = useRoute()): string {
  return resolvePageCodeFromPath(route.path) || String(route.name || '')
}

/** 获取进入当前页面前的页面编码（BI page key） */
export function getPreviousPageKey(route = useRoute()): string {
  const fromQuery = String(route.query.first_page || route.query.internal_first_traffic_source_page || '')
  if (fromQuery) return fromQuery

  if (import.meta.client) {
    return sessionStorage.getItem('bi_previous_page_key') || ''
  }

  return ''
}

